import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Form, Input, Row, Select } from "antd";
import BasicModal from "@components/core/modals/BasicModal";
import BasicButton from "@components/core/buttons/BasicButton";
import { toastError } from "@utils/helpers/toast-helper";

interface SiretValues {
  siren: string;
  nic: string;
}

interface AddGroupModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (values: SiretValues) => void;
  title: string;
  sirens: string[];
}

const AddGroupModal: FunctionComponent<AddGroupModalProps> = ({
  visible,
  onCancel,
  onOk,
  title,
  sirens,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [nic, setNic] = useState<string>("");

  const handleOk = () => {
    form
      .validateFields()
      .then((values: SiretValues) => {
        onOk(values);
        form.resetFields();
      })
      .catch(() => {
        toastError("Une erreur est survenue lors de la saisie du SIREN / NIC");
      });
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setNic("");
    }
  }, [visible, form]);

  const handleNicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.length <= 5) {
      setNic(value);
      form.setFieldsValue({ nic: value });
    }
  };

  return (
    <BasicModal
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      title={title}
      closable={false}
      footer={
        <div className="modal-footer-buttons">
          <BasicButton
            variant="secondary"
            text={t("buttons.cancel")}
            onClick={onCancel}
          />
          <BasicButton
            variant="primary"
            text={t("buttons.validate")}
            onClick={handleOk}
            disabled={nic.length !== 5}
          />
        </div>
      }
      className={`app-modal-confirm ${visible ? "" : "modal-empty"}`}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={8}>
            <span>{t("offices.group.headers.membership")}</span>
          </Col>
          <Col span={8}>
            <Form.Item
              name="siren"
              required={true}
              rules={[{ required: true, message: "Please select the SIREN!" }]}
            >
              <Select disabled={sirens.length === 0}>
                {sirens.map((siren) => (
                  <Select.Option key={siren} value={siren}>
                    {siren}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="nic"
              required={true}
              rules={[
                { required: true, message: "Please input the NIC!" },
                { len: 5, message: "NIC must be 5 characters long!" },
              ]}
            >
              <Input
                placeholder="NIC"
                value={nic}
                onChange={handleNicChange}
                maxLength={5}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </BasicModal>
  );
};

export default AddGroupModal;
